import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import useAuthStore from '../../store/authStore';
import api from '../../services/api';
import usePageTitle from '../../hooks/usePageTitle';
import GoogleSignInButton from '../../components/auth/GoogleSignInButton';
import useGoogleLoginEnabled from '../../hooks/useGoogleLoginEnabled';

export default function Login() {
    usePageTitle('Connexion');
    const [email, setEmail] = useState(() => new URLSearchParams(window.location.search).get('email') || '');
    const [password, setPassword] = useState('');
    const [info, setInfo] = useState(null);
    const [notVerified, setNotVerified] = useState(false);
    const [showForgot, setShowForgot] = useState(false);
    const [forgotLoading, setForgotLoading] = useState(false);
    const [forgotMsg, setForgotMsg] = useState(null);
    const [resendStatus, setResendStatus] = useState(null);
    const { login, loginWithGoogle, resendVerification, markOnboarded, loading, error, clearError } = useAuthStore();
    const navigate = useNavigate();
    const googleEnabled = useGoogleLoginEnabled();
    const [forceGoogle, setForceGoogle] = useState(false); // forcé si compte Google-only
    const [searchParams] = useSearchParams();
    // Même tag d'acquisition (?from=) qu'à l'inscription : un premier passage par
    // « Continuer avec Google » depuis la connexion crée aussi un compte.
    const fromParam = (searchParams.get('from') || '').toLowerCase();
    const acquisitionSource = /^[a-z0-9][a-z0-9_-]{0,62}$/.test(fromParam) ? fromParam : null;
    // Préserve email (invitation) + redirect vers le lien "Créer un compte".
    const registerQs = (() => {
        const p = new URLSearchParams();
        const em = searchParams.get('email'); if (em) p.set('email', em);
        const rd = searchParams.get('redirect'); if (rd) p.set('redirect', rd);
        const s = p.toString();
        return s ? `?${s}` : '';
    })();

    useEffect(() => {
        const activated = searchParams.get('activated');
        const reason = searchParams.get('reason');

        if (activated === 'true') {
            setInfo('Votre compte a été activé avec succès ! Vous pouvez maintenant vous connecter.');
        } else if (activated === 'already') {
            setInfo('Votre compte est déjà activé. Connectez-vous.');
        }

        if (reason === 'session_replaced') {
            setInfo('Vous avez été déconnecté car une autre session a été ouverte avec votre compte.');
        } else if (reason === 'expired') {
            setInfo('Votre session a expiré. Veuillez vous reconnecter.');
        }
    }, [searchParams]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setNotVerified(false);
        setResendStatus(null);

        const result = await login(email, password);
        if (result.success) {
            goAfterAuth(result.onboarded, result.resumeScope);
        } else if (result.code === 'NOT_VERIFIED') {
            setNotVerified(true);
        } else if (result.code === 'USE_GOOGLE') {
            setForceGoogle(true);
            setInfo('Ce compte utilise la connexion Google — clique sur « Continuer avec Google » ci-dessous.');
        }
    };

    // Redirection post-connexion, mutualisée entre mot de passe et Google.
    // onboarded === false → nouvel inscrit : on l'envoie sur l'accueil guidé.
    // resumeScope (serveur) : abonnement unique, sinon thématique de la dernière réponse.
    const goAfterAuth = (onboarded = true, resumeScope = null) => {
        // Whitelist : uniquement les paths internes (anti open-redirect via ?redirect=https://evil.com).
        const rawRedirect = searchParams.get('redirect');
        const isSafe = rawRedirect && rawRedirect.startsWith('/') && !rawRedirect.startsWith('//');
        // Un lien précis (mail : bilan, session en pause…) l'emporte sur l'accueil guidé,
        // qui reviendra à la prochaine connexion sans destination.
        let prefs = {};
        try { prefs = JSON.parse(localStorage.getItem('monquiz_dashboard_prefs') || '{}') || {}; } catch {}
        if (onboarded === false && !isSafe) {
            // Inscrit venu d'une landing (Acquisition) : directement dans sa thématique,
            // le guide reste accessible depuis le menu.
            if (prefs.landingScope) {
                markOnboarded();
                navigate(`/compte/quiz/${prefs.landingScope}`, { state: { fromAuth: true } });
                return;
            }
            navigate('/compte/guide', { state: { fromAuth: true } });
            return;
        }
        let defaultRedirect = '/compte/quiz/mes-thematiques';
        // Le hub n'est une destination que faute de mieux.
        if (prefs.lastScope && prefs.lastScope !== 'mes-thematiques') defaultRedirect = `/compte/quiz/${prefs.lastScope}`;
        if (resumeScope) defaultRedirect = `/compte/quiz/${resumeScope}`;
        navigate(isSafe ? rawRedirect : defaultRedirect, { state: { fromAuth: true } });
    };

    const handleGoogleCredential = async (credential) => {
        const result = await loginWithGoogle(credential, acquisitionSource);
        if (result.success) goAfterAuth(result.onboarded, result.resumeScope);
    };

    const handleResend = async () => {
        setResendStatus('sending');
        const result = await resendVerification(email);
        setResendStatus(result.success ? 'sent' : 'error');
    };

    return (
        <div className="auth-page">
            <div className="auth-card">
                <h1>Connexion</h1>

                {info && (
                    <div className="alert alert-info">
                        {info}
                        <button onClick={() => setInfo(null)} className="alert-close">&times;</button>
                    </div>
                )}

                {error && (
                    <div className="alert alert-error">
                        {error}
                        <button onClick={clearError} className="alert-close">&times;</button>
                    </div>
                )}

                {notVerified && (
                    <div className="alert alert-warning">
                        <p>Votre compte n'est pas encore activé.</p>
                        {resendStatus === 'sent' ? (
                            <p className="resend-ok">Un nouveau lien d'activation a été envoyé !</p>
                        ) : (
                            <button
                                onClick={handleResend}
                                className="btn-resend"
                                disabled={resendStatus === 'sending'}
                            >
                                {resendStatus === 'sending' ? 'Envoi...' : 'Renvoyer le lien d\'activation'}
                            </button>
                        )}
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label htmlFor="email">Email</label>
                        <input
                            id="email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            autoFocus
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="password">Mot de passe</label>
                        <input
                            id="password"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            minLength={8}
                        />
                    </div>

                    <button type="submit" className="btn btn-primary" disabled={loading}>
                        {loading ? 'Connexion...' : 'Se connecter'}
                    </button>
                </form>

                <p className="auth-link auth-link--forgot">
                    <span className="auth-forgot-link" onClick={() => setShowForgot(true)}>Mot de passe oublié ?</span>
                </p>
                {showForgot && (
                    <div className="auth-forgot-form">
                        <p style={{ fontSize: '0.88em', marginBottom: '10px' }}>
                            Un lien de réinitialisation sera envoyé à <strong>{email || '...'}</strong>
                        </p>
                        <button className="btn btn-primary btn-sm" disabled={forgotLoading || !email}
                            onClick={async () => {
                                setForgotLoading(true);
                                setForgotMsg(null);
                                try {
                                    const { data } = await api.post('/auth/forgot-password.php', { email });
                                    setForgotMsg({ type: 'success', text: data.message });
                                } catch (err) {
                                    setForgotMsg({ type: 'error', text: err.response?.data?.message || 'Erreur' });
                                }
                                setForgotLoading(false);
                            }}>
                            {forgotLoading ? 'Envoi...' : 'Envoyer le lien de réinitialisation'}
                        </button>
                        {!email && <p style={{ fontSize: '0.8em', color: 'var(--danger)', marginTop: '6px' }}>Saisissez votre email ci-dessus.</p>}
                        {forgotMsg && <div className={`alert alert-${forgotMsg.type}`} style={{ marginTop: '8px', fontSize: '0.85em' }}>{forgotMsg.text}</div>}
                    </div>
                )}

                {(googleEnabled || forceGoogle) && (
                    <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '18px 0', color: 'var(--text-muted)', fontSize: '0.85em' }}>
                            <span style={{ flex: 1, height: 1, background: 'var(--border)' }} />
                            ou
                            <span style={{ flex: 1, height: 1, background: 'var(--border)' }} />
                        </div>
                        <GoogleSignInButton onCredential={handleGoogleCredential} text="continue_with" />
                    </>
                )}

                <p className="auth-link">
                    Pas encore de compte ? <Link to={`/inscription${registerQs}`}>Cr&eacute;er un compte</Link>
                </p>
            </div>
        </div>
    );
}
